import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandHeader from "../components/BrandHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Mic, Square, CheckCircle2, ArrowRight, Brain, Loader2, RotateCcw, AlertTriangle,
  Share2, Copy, Check, Trash2, Link2, Plus,
} from "lucide-react";

const BAND_TONE = {
  Normal: "bg-primary/10 text-primary border-primary/30",
  Mild: "bg-secondary/30 text-primary border-secondary",
  Moderate: "bg-[hsl(var(--chart-4)/0.25)] text-foreground border-[hsl(var(--chart-4))]",
  High: "bg-destructive/10 text-destructive border-destructive/40",
};

// Confidence thresholds — a task with confidence < 0.7 gets highlighted for
// retake; ≥ 0.9 is styled as high-confidence.
const CONF_LOW = 0.7;
const CONF_HIGH = 0.9;

function confidenceTone(c) {
  if (c == null) return "bg-muted text-muted-foreground";
  if (c >= CONF_HIGH) return "bg-primary/10 text-primary border-primary/30";
  if (c >= CONF_LOW) return "bg-secondary/40 text-primary border-secondary";
  return "bg-destructive/10 text-destructive border-destructive/40";
}

function StimulusBlock({ task }) {
  if (task.display_words) {
    return (
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Remember these words</div>
        <div className="font-display text-xl tracking-wide text-primary" data-testid="moca-encoding-words">
          {task.display_words.join(" · ")}
        </div>
      </div>
    );
  }
  if (task.display_digits) {
    return (
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Digits</div>
        <div className="font-display text-2xl tracking-widest text-primary" data-testid={`moca-digits-${task.id}`}>
          {task.display_digits}
        </div>
      </div>
    );
  }
  if (task.display_sequence) {
    return (
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Letter sequence</div>
        <div className="font-mono text-lg text-primary tracking-wider break-words" data-testid={`moca-sequence-${task.id}`}>
          {task.display_sequence}
        </div>
      </div>
    );
  }
  if (task.display_images) {
    return (
      <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Name each animal, left to right</div>
        <div className="flex flex-wrap gap-4 justify-center" data-testid="moca-naming-images">
          {task.display_images.map((img) => (
            <div key={img.id} className="flex flex-col items-center" data-testid={`moca-naming-${img.id}`}>
              <div className="text-6xl" role="img" aria-label={img.alt}>{img.emoji}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// Reusable recorder — used both in the sequential task flow and in the
// per-task retake modal on the result screen.
function useRecorder(onCaptured, minSeconds = 3, maxSeconds = 30) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTsRef = useRef(0);

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        const duration = (Date.now() - startTsRef.current) / 1000;
        if (duration < minSeconds) {
          toast.error(`Please record at least ${minSeconds} seconds`);
          setElapsed(0);
          return;
        }
        onCaptured({ blob, duration });
      };
      mediaRef.current = rec;
      rec.start();
      startTsRef.current = Date.now();
      setElapsed(0);
      setRecording(true);
      timerRef.current = setInterval(() => {
        const e = Math.floor((Date.now() - startTsRef.current) / 1000);
        setElapsed(e);
        if (maxSeconds && e >= maxSeconds) stop();
      }, 250);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  return { recording, elapsed, start, stop };
}

function TaskCard({ task, onRecorded, recorded, disabled }) {
  const rec = useRecorder(onRecorded, task.min_seconds || 3, task.max_seconds || 30);
  return (
    <Card className="mano-card" data-testid={`moca-task-${task.id}`}>
      <CardContent className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Task {task.order} · {task.title}</div>
          <div className="text-xs text-muted-foreground">{task.scored ? "Scored" : "Not scored"} · min {task.min_seconds}s</div>
        </div>
        <p className="font-display text-lg sm:text-xl leading-relaxed text-foreground mb-4">{task.instruction}</p>
        <StimulusBlock task={task} />
        <div className="flex flex-col items-center">
          <button
            type="button"
            onClick={rec.recording ? rec.stop : recorded ? null : rec.start}
            disabled={disabled || recorded}
            data-testid={`moca-record-${task.id}`}
            className={
              "h-20 w-20 rounded-full flex items-center justify-center text-primary-foreground transition-colors duration-200 " +
              (recorded ? "bg-secondary text-secondary-foreground"
                : rec.recording ? "bg-destructive mano-pulse-ring"
                : "bg-primary hover:bg-primary/90 disabled:opacity-40")
            }
          >
            {recorded ? <CheckCircle2 size={30} /> : rec.recording ? <Square size={26} /> : <Mic size={28} />}
          </button>
          <div className="mt-3 text-sm text-muted-foreground" aria-live="polite">
            {rec.recording ? `${rec.elapsed}s recording…` : recorded ? "Captured" : "Tap to record"}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Inline retake row — reveals a recorder for one task and posts to the
// /moca-voice/{response_id}/retake endpoint, hoisting the returned score
// back to the parent so all tiles refresh at once.
function RetakeRow({ task, responseId, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const rec = useRecorder(
    async ({ blob, duration }) => {
      setSubmitting(true);
      try {
        const fd = new FormData();
        fd.append("file", blob, `${task.id}.webm`);
        fd.append("task_id", task.id);
        fd.append("duration", String(duration));
        const res = await api.post(`/moca-voice/${responseId}/retake`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180000,
        });
        onUpdated(res.data);
        setOpen(false);
        toast.success("Task rescored");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Retake failed");
      } finally {
        setSubmitting(false);
      }
    },
    task.min_seconds || 3,
    task.max_seconds || 30,
  );

  if (!open) {
    return (
      <Button size="sm" variant="outline"
              onClick={() => setOpen(true)}
              disabled={submitting}
              className="rounded-full h-8"
              data-testid={`moca-retake-open-${task.id}`}>
        <RotateCcw size={12} /> Retake
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid={`moca-retake-active-${task.id}`}>
      <button
        type="button"
        onClick={rec.recording ? rec.stop : rec.start}
        disabled={submitting}
        data-testid={`moca-retake-record-${task.id}`}
        className={
          "h-9 w-9 rounded-full flex items-center justify-center text-primary-foreground " +
          (rec.recording ? "bg-destructive mano-pulse-ring" : "bg-primary hover:bg-primary/90 disabled:opacity-40")
        }
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> :
         rec.recording ? <Square size={14} /> : <Mic size={14} />}
      </button>
      <span className="text-xs text-muted-foreground" aria-live="polite">
        {submitting ? "Rescoring…" : rec.recording ? `${rec.elapsed}s` : "Tap mic to re-record"}
      </span>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}
              disabled={submitting} className="h-8 text-xs"
              data-testid={`moca-retake-cancel-${task.id}`}>
        Cancel
      </Button>
    </div>
  );
}

function ShareDialog({ responseId, open, onOpenChange }) {
  const [creating, setCreating] = useState(false);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(null); // token that was just copied
  const [existing, setExisting] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const withAbsolute = (token) =>
    new URL(`/share/moca-voice/${token}`, window.location.origin).toString();

  const loadList = async () => {
    setLoadingList(true);
    try {
      const res = await api.get(`/moca-voice/${responseId}/shares`);
      setExisting((res.data || []).map((s) => ({ ...s, url: withAbsolute(s.token) })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load active links");
    } finally {
      setLoadingList(false);
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      const res = await api.post(`/moca-voice/${responseId}/share`, { ttl_days: 30 });
      const absolute = withAbsolute(res.data.token);
      setShare({ ...res.data, url: absolute });
      // Re-fetch the list so the freshly-created link appears in the manager.
      loadList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share link");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    setRevoking(token);
    try {
      await api.post(`/moca-voice/shares/${token}/revoke`);
      setExisting((l) => l.filter((s) => s.token !== token));
      if (share?.token === token) setShare(null);
      toast.success("Link revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    } finally {
      setRevoking(null);
    }
  };

  useEffect(() => {
    if (open) {
      loadList();
    } else {
      setShare(null);
      setCopied(null);
      setExisting([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, responseId]);

  const copyUrl = async (url, token) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      toast.success("Link copied");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Could not copy — please select the URL manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="moca-share-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 size={16} /> Share your voice-MoCA result
          </DialogTitle>
          <DialogDescription>
            Anyone with a link can view your score, per-task transcripts and
            rubric — but never your audio recordings. Each link expires in 30
            days and you can revoke it at any time.
          </DialogDescription>
        </DialogHeader>

        {share && (
          <div className="space-y-2 rounded-lg border border-primary/40 bg-primary/5 p-3"
               data-testid="moca-share-fresh">
            <div className="text-[10px] uppercase tracking-wider text-primary flex items-center gap-1">
              <Check size={11} /> Just created
            </div>
            <div className="flex gap-2">
              <Input readOnly value={share.url}
                     onFocus={(e) => e.target.select()}
                     className="text-sm font-mono"
                     data-testid="moca-share-url" />
              <Button type="button"
                      onClick={() => copyUrl(share.url, share.token)}
                      className="rounded-md shrink-0"
                      data-testid="moca-share-copy-btn">
                {copied === share.token ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground" data-testid="moca-share-expires">
              Expires {String(share.expires_at || "").slice(0, 10)}
              {" · "}Read-only · No audio · No personal name
            </div>
          </div>
        )}

        <div className="space-y-2" data-testid="moca-share-manager">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Link2 size={12} /> Active links
              {existing.length > 0 && (
                <Badge variant="outline" className="text-[10px] py-0 px-1.5 ml-1"
                       data-testid="moca-share-active-count">
                  {existing.length}
                </Badge>
              )}
            </div>
            <Button size="sm" onClick={create} disabled={creating}
                    className="h-7 rounded-full text-xs mano-lift"
                    data-testid="moca-share-create-btn">
              {creating ? <><Loader2 size={12} className="animate-spin" /> Creating…</> :
                          <><Plus size={12} /> New link</>}
            </Button>
          </div>

          {loadingList && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2"
                 data-testid="moca-share-loading">
              <Loader2 size={14} className="animate-spin" /> Loading active links…
            </div>
          )}

          {!loadingList && existing.length === 0 && (
            <div className="text-sm text-muted-foreground py-3 text-center rounded-lg border border-dashed border-border/60"
                 data-testid="moca-share-empty">
              No active links yet. Create one above to share this result.
            </div>
          )}

          {!loadingList && existing.length > 0 && (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {existing.map((s) => (
                <div key={s.token}
                     className="rounded-lg border border-border/60 p-3 text-sm space-y-2"
                     data-testid={`moca-share-item-${s.token}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] font-mono text-muted-foreground truncate">
                      …{s.token.slice(-10)}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
                      Expires {String(s.expires_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input readOnly value={s.url}
                           onFocus={(e) => e.target.select()}
                           className="text-xs font-mono h-8" />
                    <Button size="sm" variant="ghost" onClick={() => copyUrl(s.url, s.token)}
                            className="h-8 rounded-md shrink-0"
                            data-testid={`moca-share-copy-${s.token}`}>
                      {copied === s.token ? <Check size={13} /> : <Copy size={13} />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => revoke(s.token)}
                            disabled={revoking === s.token}
                            className="h-8 rounded-md shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            data-testid={`moca-share-revoke-${s.token}`}>
                      {revoking === s.token ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="moca-share-close-btn">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultScreen({ score, initialTasks, responseId, taskCatalogue, onDone }) {
  const [tasks, setTasks] = useState(initialTasks || []);
  const [currentScore, setCurrentScore] = useState(score);
  const [pendingPath, setPendingPath] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const totalMax = currentScore?.total_max || 30;

  const catalogueById = React.useMemo(() => {
    const m = {};
    for (const t of taskCatalogue) m[t.id] = t;
    return m;
  }, [taskCatalogue]);

  const scoredTasks = tasks.filter((t) => t.scored !== false);
  const lowConfTasks = scoredTasks.filter(
    (t) => (t.rubric?.confidence ?? 1) < CONF_LOW,
  );
  const lowConfCount = lowConfTasks.length;

  // Warn respondents leaving the tab while low-confidence tasks are still
  // pending. Cannot cancel the navigation programmatically in modern
  // browsers — this fires the built-in "Leave site?" dialog.
  useEffect(() => {
    if (lowConfCount === 0) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [lowConfCount]);

  const handleRetakeUpdate = (data) => {
    setCurrentScore(data.score);
    setTasks(data.tasks || []);
  };

  const requestLeave = (path) => {
    if (lowConfCount > 0) {
      setPendingPath(path);
    } else {
      onDone(path);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-12">
        <div className="mb-8">
          <h1 className="font-display font-semibold text-3xl tracking-tight" data-testid="moca-result-title">
            Your voice-MoCA score
          </h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            A clinician will review these results alongside your other screenings.
            {lowConfCount > 0 && (
              <> The model wasn't fully sure about <b>{lowConfCount}</b> task{lowConfCount === 1 ? "" : "s"}
              — you can re-record any of them below and your score will update automatically.</>
            )}
          </p>
        </div>

        {lowConfCount > 0 && (
          <div className="mb-6 rounded-lg border border-[hsl(var(--chart-4))] bg-[hsl(var(--chart-4)/0.15)] p-4 flex items-start gap-3"
               data-testid="moca-low-conf-banner">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[hsl(var(--chart-4))]" aria-hidden="true" />
            <div className="flex-1">
              <div className="text-sm font-medium">
                {lowConfCount} task{lowConfCount === 1 ? "" : "s"} under 70% confidence
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Re-record any of the flagged tasks below to improve your score. Leaving now keeps your current total.
              </div>
              <div className="mt-2 flex flex-wrap gap-1"
                   data-testid="moca-low-conf-list">
                {lowConfTasks.map((t) => (
                  <Badge key={t.id} variant="outline"
                         className="text-[10px] border-[hsl(var(--chart-4))] text-[hsl(var(--chart-4))]"
                         data-testid={`moca-low-conf-tag-${t.id}`}>
                    {(catalogueById[t.id]?.title) || t.id.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        <Card className="mano-card mb-6">
          <CardContent className="p-6 md:p-8 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Total</div>
                <div className="font-display text-4xl leading-none" data-testid="moca-result-total">
                  {currentScore.total}
                  <span className="text-muted-foreground text-lg"> / {totalMax}</span>
                </div>
              </div>
              <Badge className={"border text-sm px-3 py-1 " + (BAND_TONE[currentScore.band] || "")}
                     data-testid={`moca-result-band-${currentScore.band}`}>
                {currentScore.band}
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">{currentScore.label}</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {(currentScore.domain_scores || []).map((d) => (
                <div key={d.key}
                     className="flex items-center justify-between rounded-lg border border-border/60 p-3"
                     data-testid={`moca-domain-${d.key}`}>
                  <div className="text-sm">{d.label}</div>
                  <div className="font-display text-base">
                    {d.score}<span className="text-muted-foreground"> / {d.max}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button onClick={() => requestLeave("/questionnaires")}
                      className="rounded-full px-6 mano-lift"
                      data-testid="moca-continue-btn">
                Continue to questionnaires <ArrowRight size={16} />
              </Button>
              <Button variant="outline" onClick={() => requestLeave("/results")}
                      className="rounded-full px-6"
                      data-testid="moca-view-results-btn">
                View all results
              </Button>
              <Button variant="outline" onClick={() => setShareOpen(true)}
                      className="rounded-full px-6"
                      data-testid="moca-share-open-btn">
                <Share2 size={14} /> Share with GP or family
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mano-card">
          <CardContent className="p-6 md:p-8">
            <h2 className="font-display font-semibold text-lg mb-1">Per-task confidence</h2>
            <p className="text-xs text-muted-foreground mb-4">
              The scorer returns a confidence level for each task. Retake any task
              you'd like to be more confident on.
            </p>
            <div className="space-y-3">
              {scoredTasks.map((t) => {
                const rub = t.rubric || {};
                const conf = rub.confidence ?? 0;
                const overridden = !!rub.override;
                const eff = overridden ? rub.override.score : rub.score;
                return (
                  <div key={t.id}
                       className="rounded-lg border border-border/60 p-3"
                       data-testid={`moca-conf-row-${t.id}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="text-sm font-medium capitalize">
                        {(catalogueById[t.id]?.title) || t.id.replace(/_/g, " ")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={"border text-[10px] px-2 py-0.5 " + confidenceTone(conf)}
                               data-testid={`moca-conf-badge-${t.id}`}>
                          {(conf * 100).toFixed(0)}% confidence
                        </Badge>
                        <div className="text-xs text-muted-foreground">
                          {eff ?? "-"} / {rub.max ?? "-"}
                          {overridden && <span className="ml-1 text-secondary-foreground">(overridden)</span>}
                        </div>
                      </div>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1 overflow-hidden">
                      <div className={"h-1 " + (conf >= CONF_HIGH ? "bg-primary" : conf >= CONF_LOW ? "bg-secondary" : "bg-destructive")}
                           style={{ width: `${Math.max(4, conf * 100)}%` }} />
                    </div>
                    {rub.rationale && (
                      <div className="text-[11px] text-muted-foreground mt-2 italic">{rub.rationale}</div>
                    )}
                    <div className="mt-2">
                      <RetakeRow task={{ ...(catalogueById[t.id] || {}), id: t.id }}
                                 responseId={responseId}
                                 onUpdated={handleRetakeUpdate} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </main>

      <AlertDialog open={pendingPath !== null} onOpenChange={(o) => !o && setPendingPath(null)}>
        <AlertDialogContent data-testid="moca-leave-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave with {lowConfCount} low-confidence task{lowConfCount === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Retaking these tasks could improve your total score. If you leave now, your current score stands and the clinician will review the flagged tasks alongside your other screenings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="moca-leave-stay-btn">Stay and retake</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const p = pendingPath; setPendingPath(null); onDone(p); }}
              data-testid="moca-leave-continue-btn">
              Leave anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShareDialog responseId={responseId} open={shareOpen} onOpenChange={setShareOpen} />
    </div>
  );
}

export default function RespondentMocaVoice() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [idx, setIdx] = useState(0);
  const [captures, setCaptures] = useState({}); // task_id: { blob, duration }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { score, tasks, response_id }

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/moca-voice/tasks")
      .then((r) => setTasks(r.data?.tasks || []))
      .catch(() => toast.error("Could not load voice-MoCA tasks"));
  }, [user, loading, nav]);

  const currentTask = tasks[idx];
  const allCaptured = tasks.length > 0 && tasks.every((t) => captures[t.id]);

  const handleRecorded = ({ blob, duration }) => {
    setCaptures((c) => ({ ...c, [currentTask.id]: { blob, duration } }));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      tasks.forEach((t) => {
        const cap = captures[t.id];
        if (!cap) return;
        fd.append("files", cap.blob, `${t.id}.webm`);
        fd.append("task_ids", t.id);
        fd.append("durations", String(cap.duration));
      });
      const res = await api.post("/moca-voice/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 240000,
      });
      setSubmitted({
        score: res.data?.score,
        tasks: res.data?.voice_moca?.tasks || [],
        response_id: res.data?.response_id,
      });
      toast.success("Voice-MoCA scored");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <ResultScreen
        score={submitted.score}
        initialTasks={submitted.tasks}
        responseId={submitted.response_id}
        taskCatalogue={tasks}
        onDone={(path) => nav(path)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <BrandHeader />
      <main className="mano-container-narrow py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
            <Brain size={14} /> Brain Health · MoCA-inspired · 30-point
          </div>
          <h1 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight" data-testid="moca-page-title">
            Voice-driven cognitive screening
          </h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">
            About eighteen short voice tasks across seven cognitive domains.
            We transcribe each recording and a clinical language model scores
            your responses against a rubric. Please speak clearly in a quiet
            space. Allow roughly 10 minutes.
            {user?.preferred_language && user.preferred_language !== "en" && (
              <> You may answer in <b>{user.preferred_language.toUpperCase()}</b> for
              semantic items — the scorer accepts your preferred language.</>
            )}
          </p>
        </div>

        {tasks.length === 0 ? (
          <div className="text-muted-foreground">Loading tasks…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">
                Task {idx + 1} of {tasks.length}
              </div>
              <div className="flex gap-1 flex-wrap justify-end max-w-[60%]">
                {tasks.map((t, i) => (
                  <span key={t.id}
                        className={"h-1.5 w-4 rounded-full " +
                          (captures[t.id] ? "bg-primary" : i === idx ? "bg-primary/50" : "bg-border")} />
                ))}
              </div>
            </div>

            {currentTask && (
              <TaskCard task={currentTask} onRecorded={handleRecorded}
                        recorded={!!captures[currentTask.id]} disabled={submitting} />
            )}

            <div className="flex justify-between gap-3">
              <Button variant="outline"
                      disabled={idx === 0 || submitting}
                      onClick={() => setIdx((i) => Math.max(0, i - 1))}
                      className="rounded-full px-6"
                      data-testid="moca-prev-btn">
                Previous
              </Button>
              {idx < tasks.length - 1 ? (
                <Button disabled={!captures[currentTask?.id] || submitting}
                        onClick={() => setIdx((i) => i + 1)}
                        className="rounded-full px-6 mano-lift"
                        data-testid="moca-next-btn">
                  Next task <ArrowRight size={16} />
                </Button>
              ) : (
                <Button disabled={!allCaptured || submitting}
                        onClick={submit}
                        className="rounded-full px-6 mano-lift"
                        data-testid="moca-submit-btn">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Scoring…</> :
                                <>Submit for scoring <ArrowRight size={16} /></>}
                </Button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
